export type OldTownFacadeTreatment =
  | "pastel_dormered"
  | "classical_arcade"
  | "gedimino_avenue";

export type OldTownDoorLeafStyle = "timber" | "painted";
export type OldTownDoorPanelPattern = "diamond" | "herringbone";

export interface OldTownBuildingArtPoint {
  readonly x: number;
  readonly z: number;
}

export interface OldTownStreetDetailProfile {
  readonly frontagePhase: number;
  readonly hasBalcony: boolean;
  readonly hasShopCanopy: boolean;
  readonly hasPortalLantern: boolean;
  readonly hasWindowPlanters: boolean;
  readonly downpipeSide: "left" | "right";
  readonly doorLeafStyle: OldTownDoorLeafStyle;
  readonly doorPanelPattern: OldTownDoorPanelPattern;
}

export interface OldTownCourtyardDetailProfile {
  readonly paved: boolean;
  readonly hasWindowPlanters: boolean;
  readonly classicalWindowSurrounds: boolean;
  readonly hasGroundPlanter: boolean;
  readonly hasBench: boolean;
}

/**
 * Project-owned art-direction zone for the historic-centre building grammar.
 *
 * The deliberately coarse, rounded outline was authored with the public
 * Vilnius municipal planning layer "Vilniaus senamiesčio riba" as a visual
 * QA guide:
 * https://atviras.vplanas.lt/arcgis/rest/services/mapA4/Bendrasis_planas/MapServer/51
 *
 * It is not a legal, cadastral, heritage, or administrative boundary. Runtime
 * coordinates are metres from the documented EPSG:3346 project origin.
 */
export const OLD_TOWN_BUILDING_ART_ZONE = Object.freeze([
  { x: -325, z: 850 },
  { x: 100, z: 2025 },
  { x: 1650, z: 1450 },
  { x: 1825, z: 950 },
  { x: 1525, z: 625 },
  { x: 1700, z: -300 },
  { x: 1275, z: -425 },
  { x: 750, z: -25 },
  { x: 125, z: -300 },
  { x: -25, z: 625 },
] as const satisfies readonly OldTownBuildingArtPoint[]);

export const OLD_TOWN_DETAIL_MINIMUM_FRONTAGE_M = 5.5;
export const OLD_TOWN_DETAIL_MAXIMUM_STREET_DISTANCE_M = 60;
export const OLD_TOWN_DOOR_MINIMUM_FRONTAGE_M = 7;
export const OLD_TOWN_BALCONY_MINIMUM_FRONTAGE_M = 10;
export const OLD_TOWN_WINDOW_SURROUND_MINIMUM_FRONTAGE_M = 7;
export const OLD_TOWN_WINDOW_SURROUND_MAXIMUM_COLUMNS = 10;
export const OLD_TOWN_WINDOW_PANEL_OFFSET_M = 0.18;
export const OLD_TOWN_WINDOW_FRAME_DEPTH_M = 0.24;
export const OLD_TOWN_WINDOW_MULLION_DEPTH_M = 0.22;
export const OLD_TOWN_WINDOW_SILL_DEPTH_M = 0.36;
export const OLD_TOWN_CHIMNEY_SPACING_M = 13;
export const OLD_TOWN_CHIMNEY_MAXIMUM_COUNT = 3;
export const OLD_TOWN_COURTYARD_DETAIL_MINIMUM_AREA_M2 = 40;
export const OLD_TOWN_COURTYARD_DETAIL_MINIMUM_EDGE_M = 5;
export const OLD_TOWN_COURTYARD_PLINTH_HEIGHT_M = 0.48;
export const OLD_TOWN_COURTYARD_EAVE_HEIGHT_M = 0.24;
export const OLD_TOWN_COURTYARD_FURNITURE_MINIMUM_EDGE_M = 10;
export const OLD_TOWN_GUTTER_HEIGHT_M = 0.14;
export const OLD_TOWN_GUTTER_DEPTH_M = 0.2;
export const OLD_TOWN_ROOF_WINDOW_SPACING_M = 8;
export const OLD_TOWN_ROOF_WINDOW_MAXIMUM_COUNT = 4;
export const OLD_TOWN_ROOF_WINDOW_EDGE_CLEARANCE_M = 1.5;
export const OLD_TOWN_ROOF_WINDOW_WIDTH_M = 0.94;
export const OLD_TOWN_ROOF_WINDOW_SLOPE_HEIGHT_M = 1.32;
export const OLD_TOWN_ROOF_WINDOW_FRAME_MARGIN_M = 0.16;
export const OLD_TOWN_ROOF_WINDOW_SURFACE_OFFSET_M = 0.07;
export const OLD_TOWN_ROOF_WINDOW_SLOPE_PROGRESS = 0.48;
export const OLD_TOWN_COMPLEX_ROOF_APRON_MINIMUM_RUN_M = 2.8;
export const OLD_TOWN_COMPLEX_ROOF_APRON_MAXIMUM_RUN_M = 5.2;

/**
 * Source-preserving art corridor for Gedimino Avenue.
 *
 * The polyline is a checked-in, rounded runtime-space transcription of the
 * named GRPK `Gedimino pr.` road segments in
 * `data/fixtures/source/m1-old-town-aoi.json`. It selects a facade grammar; it
 * does not replace the official road or building footprints.
 */
export const GEDIMINO_AVENUE_BUILDING_ART_REFERENCE_LINE = Object.freeze([
  { x: 0, z: 108.83 },
  { x: 20.67, z: 114.61 },
  { x: 61.28, z: 125.47 },
  { x: 101.64, z: 136.63 },
  { x: 152.44, z: 151.2 },
  { x: 191.61, z: 161.87 },
  { x: 258.67, z: 182.06 },
  { x: 281.79, z: 189.01 },
  { x: 315.78, z: 198.46 },
  { x: 318.68, z: 199.27 },
  { x: 471.58, z: 244.88 },
  { x: 530.12, z: 262.51 },
] as const satisfies readonly OldTownBuildingArtPoint[]);

export const GEDIMINO_AVENUE_BUILDING_ART_DISTANCE_M = 44;

export function isWithinGediminoAvenueBuildingArtCorridor(
  point: Readonly<OldTownBuildingArtPoint>,
): boolean {
  for (
    let index = 1;
    index < GEDIMINO_AVENUE_BUILDING_ART_REFERENCE_LINE.length;
    index += 1
  ) {
    const from = GEDIMINO_AVENUE_BUILDING_ART_REFERENCE_LINE[index - 1];
    const to = GEDIMINO_AVENUE_BUILDING_ART_REFERENCE_LINE[index];
    if (
      from !== undefined &&
      to !== undefined &&
      distanceToSegment(point, from, to) <=
        GEDIMINO_AVENUE_BUILDING_ART_DISTANCE_M
    ) {
      return true;
    }
  }
  return false;
}

export function isWithinOldTownBuildingArtZone(
  point: Readonly<OldTownBuildingArtPoint>,
): boolean {
  let inside = false;
  for (
    let currentIndex = 0, previousIndex = OLD_TOWN_BUILDING_ART_ZONE.length - 1;
    currentIndex < OLD_TOWN_BUILDING_ART_ZONE.length;
    previousIndex = currentIndex, currentIndex += 1
  ) {
    const current = OLD_TOWN_BUILDING_ART_ZONE[currentIndex];
    const previous = OLD_TOWN_BUILDING_ART_ZONE[previousIndex];
    if (current === undefined || previous === undefined) {
      continue;
    }
    if (pointIsOnSegment(point, previous, current)) {
      return true;
    }
    const crossesLatitude =
      (current.z > point.z) !== (previous.z > point.z);
    if (!crossesLatitude) {
      continue;
    }
    const crossingX =
      (previous.x - current.x) *
        (point.z - current.z) /
        (previous.z - current.z) +
      current.x;
    if (point.x < crossingX) {
      inside = !inside;
    }
  }
  return inside;
}

export function oldTownFacadeTreatmentForBuilding(
  buildingId: string,
  globalCentroid: Readonly<OldTownBuildingArtPoint>,
): OldTownFacadeTreatment | null {
  if (!isWithinOldTownBuildingArtZone(globalCentroid)) {
    return null;
  }
  if (isWithinGediminoAvenueBuildingArtCorridor(globalCentroid)) {
    return "gedimino_avenue";
  }
  // Most historic-centre buildings receive the quieter plaster-and-dormer
  // family; the classical family breaks up long streets with arcades and
  // stronger pilaster rhythm. IDs make the mix stable across streaming.
  return stableTextSeed(buildingId) % 5 < 3
    ? "pastel_dormered"
    : "classical_arcade";
}

function distanceToSegment(
  point: Readonly<OldTownBuildingArtPoint>,
  from: Readonly<OldTownBuildingArtPoint>,
  to: Readonly<OldTownBuildingArtPoint>,
): number {
  const deltaX = to.x - from.x;
  const deltaZ = to.z - from.z;
  const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
  if (lengthSquared <= Number.EPSILON) {
    return Math.hypot(point.x - from.x, point.z - from.z);
  }
  const progress = Math.max(
    0,
    Math.min(
      1,
      ((point.x - from.x) * deltaX +
        (point.z - from.z) * deltaZ) / lengthSquared,
    ),
  );
  return Math.hypot(
    point.x - (from.x + deltaX * progress),
    point.z - (from.z + deltaZ * progress),
  );
}

/**
 * Stable, deliberately sparse street-wall accents based on the recurring
 * silhouettes visible in public Vilnius Old Town references. These are an art
 * grammar, not claims about the exact balcony or shop use of a source building.
 */
export function oldTownStreetDetailProfileForBuilding(
  buildingId: string,
): OldTownStreetDetailProfile {
  const seed = stableTextSeed(`${buildingId}:street-details`);
  return {
    frontagePhase: ((seed >>> 8) & 0xffff) / 0xffff,
    hasBalcony: seed % 4 === 0,
    hasShopCanopy: (seed >>> 4) % 5 === 0,
    hasPortalLantern: (seed & 0x100000) !== 0,
    hasWindowPlanters: (seed >>> 10) % 3 !== 0,
    downpipeSide: (seed & 0x100) === 0 ? "left" : "right",
    doorLeafStyle: (seed >>> 13) % 3 === 0 ? "painted" : "timber",
    doorPanelPattern: (seed & 0x20000) === 0
      ? "diamond"
      : "herringbone",
  };
}

/**
 * Stable courtyard accents informed by recurring Old Town conditions: most
 * larger enclosed courts are paved while some remain garden-like, and only a
 * subset receive the more formal surrounds. The source footprint still owns
 * the court shape; this profile only selects its restrained art treatment.
 */
export function oldTownCourtyardDetailProfileForBuilding(
  buildingId: string,
): OldTownCourtyardDetailProfile {
  const seed = stableTextSeed(`${buildingId}:courtyard-details`);
  const hasWindowPlanters = (seed >>> 5) % 2 === 0;
  const classicalWindowSurrounds = (seed >>> 3) % 5 === 0;
  const paved = seed % 4 !== 0;
  return {
    paved,
    hasWindowPlanters,
    classicalWindowSurrounds,
    // Quiet residential courts get one wall-side planting accent while the
    // formal arcade family stays architecturally restrained.
    hasGroundPlanter: hasWindowPlanters && !classicalWindowSurrounds,
    // A sparse seating rhythm keeps paved courts useful without turning every
    // source hole into the same furnished plaza.
    hasBench: paved && (seed >>> 8) % 3 === 0,
  };
}

function stableTextSeed(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function pointIsOnSegment(
  point: Readonly<OldTownBuildingArtPoint>,
  from: Readonly<OldTownBuildingArtPoint>,
  to: Readonly<OldTownBuildingArtPoint>,
): boolean {
  const cross =
    (point.z - from.z) * (to.x - from.x) -
    (point.x - from.x) * (to.z - from.z);
  if (Math.abs(cross) > 0.001) {
    return false;
  }
  const dot =
    (point.x - from.x) * (to.x - from.x) +
    (point.z - from.z) * (to.z - from.z);
  if (dot < 0) {
    return false;
  }
  const lengthSquared =
    (to.x - from.x) ** 2 +
    (to.z - from.z) ** 2;
  return dot <= lengthSquared;
}
